import { TheorieResponse } from "@/services/api_types";

/**
 * Client-side theorie data for the demo (Pythagoras driehoek).
 * IDs are negative to distinguish from real API theorie IDs.
 * Order matches driehoekDemo.theorieSamenvatting:
 *   -1 = Stelling van Pythagoras
 *   -2 = Worteltrekken
 *
 * Content is plain HTML in the same shape that GeneratedTheorieWidget
 * expects: a flat sequence of <section><h2>Title</h2>...</section> blocks.
 * The first section is auto-expanded; math uses $..$ / $$..$$ delimiters
 * which KaTeX renders client-side.
 */
export const DEMO_THEORIEEN: Record<number, TheorieResponse> = {
  [-1]: {
    id: -1,
    titel: "Stelling van Pythagoras",
    content: {
      titel: "Stelling van Pythagoras",
      html: `
        <section>
          <h2>Wat is de stelling van Pythagoras?</h2>
          <p>In een <strong>rechthoekige driehoek</strong> geldt altijd:</p>
          <p>$$a^2 + b^2 = c^2$$</p>
          <img src="/theorie-pythagoras.svg" alt="Pythagoras driehoek" style="max-width: 70%; border: 1px solid #e5e7eb;">
          <p>Hierin zijn $a$ en $b$ de twee <strong>rechthoekszijden</strong> (de zijden die de rechte hoek vormen) en $c$ is de <strong>schuine zijde</strong> (hypotenusa). De schuine zijde ligt altijd tegenover de hoek van $90°$ en is de langste zijde van de driehoek.</p>
          <p>De stelling werkt alleen als de driehoek een rechte hoek heeft. Controleer dat dus altijd eerst!</p>
        </section>
        <section>
          <h2>Wanneer gebruik je het?</h2>
          <p>Gebruik de stelling van Pythagoras als je:</p>
          <ul>
            <li>Twee zijden van een rechthoekige driehoek kent en de derde zoekt.</li>
            <li>Wilt controleren of een driehoek rechthoekig is (dan moet $a^2 + b^2 = c^2$ kloppen).</li>
          </ul>
          <p><strong>Let op:</strong> de schuine zijde staat altijd alleen aan één kant van het $=$-teken.</p>
        </section>
        <section>
          <h2>De schuine zijde berekenen</h2>
          <p>Als je de twee rechthoekszijden kent ($a$ en $b$), begin je met:</p>
          <p>$$c^2 = a^2 + b^2$$</p>
          <p>Daarna trek je de wortel om $c$ te vinden:</p>
          <p>$$c = \\sqrt{a^2 + b^2}$$</p>
          <p><strong>Voorbeeld:</strong> $a = 3$ en $b = 4$</p>
          <p>$$c^2 = 3^2 + 4^2 = 9 + 16 = 25$$</p>
          <p>$$c = \\sqrt{25} = 5$$</p>
        </section>
        <section>
          <h2>Een rechthoekszijde berekenen</h2>
          <p>Als je de schuine zijde en één rechthoekszijde kent, schrijf je de formule eerst om voor $a^2$:</p>
          <p>$$a^2 = c^2 - b^2$$</p>
          <p>Daarna trek je de wortel om $a$ te vinden:</p>
          <p>$$a = \\sqrt{c^2 - b^2}$$</p>
          <p><strong>Voorbeeld:</strong> $c = 13$ en $b = 5$</p>
          <p>$$a^2 = 13^2 - 5^2 = 169 - 25 = 144$$</p>
          <p>$$a = \\sqrt{144} = 12$$</p>
        </section>
        <section>
          <h2>Veelgemaakte fouten</h2>
          <ul>
            <li>De schuine zijde aan de verkeerde kant van het $=$-teken zetten.</li>
            <li>Vergeten dat $c$ altijd de <strong>langste</strong> zijde is.</li>
            <li>De wortel vergeten aan het eind: $a^2 = 144$ betekent $a = 12$, niet $a = 144$.</li>
            <li>Pythagoras toepassen op een driehoek die geen rechte hoek heeft.</li>
          </ul>
        </section>
      `,
      sections: [
        {
          titel: "Pythagoras in deze stap",
          uitgeklapt: true,
          content:
            `<div class="rounded-xl bg-slate-50 border border-slate-100 px-3 py-3 mb-3">
              <svg viewBox="0 0 360 145" role="img" aria-label="Rechthoekige driehoek ABC met rechte hoek bij C en zijden AC, BC en AB" style="display:block;width:100%;max-width:205px;margin:0 auto 4px auto;">
                <path d="M86 112 L264 112 L86 34 Z" fill="#eff6ff" stroke="none"></path>
                <path d="M86 112 L264 112 M86 112 L86 34" fill="none" stroke="#374151" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M86 34 L264 112" fill="none" stroke="#2563eb" stroke-width="3.5" stroke-linecap="round"></path>
                <path d="M86 90 L108 90 L108 112" fill="none" stroke="#6b7280" stroke-width="2.5"></path>
                <text x="77" y="30" font-size="14" font-weight="800" fill="#111827" text-anchor="middle">A</text>
                <text x="276" y="117" font-size="14" font-weight="800" fill="#111827" text-anchor="middle">B</text>
                <text x="76" y="128" font-size="14" font-weight="900" fill="#111827" text-anchor="middle">C</text>
                <text x="174" y="135" font-size="18" font-weight="800" fill="#111827" text-anchor="middle">BC</text>
                <text x="61" y="77" font-size="18" font-weight="800" fill="#111827" text-anchor="middle" transform="rotate(-90 61 77)">AC</text>
                <text x="180" y="64" font-size="19" font-weight="900" fill="#2563eb" text-anchor="middle" transform="rotate(24 180 64)">AB</text>
              </svg>
              <div style="text-align:center;font-weight:700;color:#111827;">$$AC^2 + BC^2 = AB^2$$</div>
            </div>\n\n` +
            `<div class="space-y-2">
              <p><strong>Wanneer mag je dit gebruiken?</strong><br />Alleen bij een rechthoekige driehoek. In onze opgave staat: ∠C = 90°. Dus de rechte hoek zit bij punt C en Pythagoras mag.</p>
              <p><strong>Schuine zijde</strong><br />AB ligt tegenover de rechte hoek. Daarom staat AB alleen rechts van het is-teken.</p>
            </div>`
        }
      ]
    },
    voorbeelden: [
      {
        vraag:
          "In een rechthoekige driehoek zijn de rechthoekszijden $6$ en $8$. Bereken de schuine zijde.",
        stappen_uitwerking: [
          "De schuine zijde berekenen we met $c^2 = a^2 + b^2$.",
          "Invullen: $c^2 = 6^2 + 8^2 = 36 + 64 = 100$.",
          "Wortel trekken: $c = \\sqrt{100} = 10$.",
          "De schuine zijde is $10$."
        ]
      }
    ],
    images: []
  },
  [-2]: {
    id: -2,
    titel: "Worteltrekken",
    content: {
      titel: "Worteltrekken",
      html: `
        <section>
          <h2>Wat is worteltrekken?</h2>
          <p>Worteltrekken is het omgekeerde van kwadrateren. Als je weet dat</p>
          <p>$$x^2 = k$$</p>
          <p>dan vind je $x$ door de wortel te trekken:</p>
          <p>$$x = \\sqrt{k}$$</p>
          <p>Bij meetkunde (lengtes, afstanden) is $x$ altijd positief, dus je neemt alleen de positieve wortel.</p>
        </section>
        <section>
          <h2>Wanneer gebruik je het?</h2>
          <ul>
            <li>Na het toepassen van de stelling van Pythagoras: je vindt $x^2 = \\text{getal}$ en moet nog de wortel trekken.</li>
            <li>Bij het oplossen van vergelijkingen van de vorm $x^2 = k$.</li>
            <li>Bij het berekenen van afstanden in het assenstelsel.</li>
          </ul>
        </section>
        <section>
          <h2>Exacte en afgeronde wortels</h2>
          <p>Sommige wortels zijn <strong>exact</strong> (gehele getallen):</p>
          <table>
            <thead>
              <tr><th>$k$</th><th>$\\sqrt{k}$</th></tr>
            </thead>
            <tbody>
              <tr><td>$4$</td><td>$2$</td></tr>
              <tr><td>$9$</td><td>$3$</td></tr>
              <tr><td>$16$</td><td>$4$</td></tr>
              <tr><td>$25$</td><td>$5$</td></tr>
              <tr><td>$36$</td><td>$6$</td></tr>
              <tr><td>$49$</td><td>$7$</td></tr>
              <tr><td>$64$</td><td>$8$</td></tr>
              <tr><td>$81$</td><td>$9$</td></tr>
              <tr><td>$100$</td><td>$10$</td></tr>
              <tr><td>$121$</td><td>$11$</td></tr>
              <tr><td>$144$</td><td>$12$</td></tr>
              <tr><td>$169$</td><td>$13$</td></tr>
            </tbody>
          </table>
          <p>Als de wortel niet exact is (bijvoorbeeld $\\sqrt{7}$), laat je het als wortel staan of rond je af. Lees goed wat de opgave vraagt!</p>
        </section>
        <section>
          <h2>Veelgemaakte fouten</h2>
          <ul>
            <li>Vergeten de wortel te trekken: $x^2 = 144$ dus $x = 144$. <strong>Fout!</strong> Het antwoord is $x = 12$.</li>
            <li>$\\sqrt{a + b}$ is <strong>niet</strong> gelijk aan $\\sqrt{a} + \\sqrt{b}$. Bijvoorbeeld: $\\sqrt{9 + 16} = \\sqrt{25} = 5$, maar $\\sqrt{9} + \\sqrt{16} = 3 + 4 = 7$.</li>
            <li>Een negatief getal onder de wortel: $\\sqrt{-4}$ bestaat niet (in de reële getallen).</li>
          </ul>
        </section>
      `,
      sections: [
        {
          titel: "Kort",
          uitgeklapt: true,
          content:
            "Worteltrekken is het omgekeerde van kwadrateren: als $x^2 = k$, dan $x = \\sqrt{k}$. Bij lengtes neem je alleen de positieve wortel."
        },
        {
          titel: "In deze stap",
          uitgeklapt: true,
          content:
            "We weten $x^2 = 144$. Trek aan beide kanten de wortel om $x$ te vinden:\n\n$$x = \\sqrt{144} = 12$$"
        }
      ]
    },
    voorbeelden: [
      {
        vraag: "Los op: $x^2 = 81$.",
        stappen_uitwerking: [
          "We trekken de wortel aan beide kanten: $x = \\sqrt{81}$.",
          "$x = 9$."
        ]
      },
      {
        vraag: "Bereken $\\sqrt{48}$ in de vorm $a\\sqrt{b}$.",
        stappen_uitwerking: [
          "Ontbind $48$ in factoren: $48 = 16 \\cdot 3$.",
          "Gebruik $\\sqrt{16 \\cdot 3} = \\sqrt{16} \\cdot \\sqrt{3}$.",
          "$\\sqrt{48} = 4\\sqrt{3}$."
        ]
      }
    ],
    images: []
  }
};
